import { Stack, Typography, type SxProps } from '@mui/material';
import type { ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType } from '@repo/api-gw-sdk';

import { hasValidConditionValues } from '@/components/conditions/ConditionEditor';
import {
  apps,
  sourceAccount,
  dataClasses,
  environment,
  resourceName,
  resourceType,
  sourceRegion,
  subnets,
  tags,
  vpc,
  resourceId,
  cloudProvider,
  resourceGroupName,
  tagKeys,
  tagKeyValues,
} from '@/components/queryBuilder/properties';
import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { type FilterProperty } from '@/types/advanceFilter';

import { type BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { HighFrequencyResourceTypeSelector } from './highFrequencyResourceTypeSelector';
import { HighFrequencySupportedDisclaimer } from './highFrequencySupportedDisclaimer';
import { useHighFrequencySupportedResourceTypes } from './highFrequencySupportedResourceTypes';

export const policySupportedProperties = (
  tagsFiltersV2: boolean
): FilterProperty[][] => [
  [resourceType, dataClasses, environment, apps],
  [cloudProvider, sourceAccount, sourceRegion, vpc, subnets, resourceGroupName],
  [resourceName, resourceId],
  tagsFiltersV2 ? [tagKeys, tagKeyValues] : [tags],
];

const highFrequencySupportedProperties = (
  tagsFiltersV2: boolean
): FilterProperty[][] => [
  [dataClasses, environment],
  [sourceAccount, sourceRegion],
  [resourceName, resourceId],
  tagsFiltersV2 ? [tagKeys, tagKeyValues] : [tags],
];

export const BackupPolicyConditionEditor = (
  props: StepProps<BackupPolicyEditState>
) => {
  const highFrequencySupportedResourceTypes =
    useHighFrequencySupportedResourceTypes();
  const { DynamoDBSupport, tagsFiltersV2 } = useFeatureFlags();
  const supportedProperties =
    props.wizardState.policyType ===
    BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
      ? highFrequencySupportedProperties
      : policySupportedProperties;

  return (
    <>
      <StepContainer
        sx={{ padding: 0 }}
        stepperLabels={props.stepperLabels}
        stepperIndex={0}
        canGoNext={() =>
          (props.wizardState.policyType !==
            BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ||
            !!props.wizardState.resourceTypes?.length ||
            props.wizardState.resourcesScopeType === 'allExcluded') &&
          (props.wizardState.resourcesScopeType !== 'useConditions' ||
            hasValidConditionValues(props.wizardState.condition))
        }
        onNextClick={async () => {
          if (
            props.wizardState.policyType ===
              BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY &&
            props.wizardState.resourcesScopeType === 'allExcluded'
          ) {
            props.setWizardState({
              ...props.wizardState,
              resourceTypes: highFrequencySupportedResourceTypes,
            });
          }
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        navigationComponent={
          <RenameEntityName
            label='Backup policy name'
            name={props.wizardState.name}
            onChange={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <HighFrequencySupportedDisclaimer
          policyType={props.wizardState.policyType}
        />
        <ResourcesSelectionEditor
          selectedResourceTypes={props.wizardState.resourceTypes}
          setSelectedResourceTypes={(resourceTypes) =>
            props.setWizardState({ ...props.wizardState, resourceTypes })
          }
          isEditable={
            props.wizardState.policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
          }
          condition={props.wizardState.condition}
          includedResources={props.wizardState.include}
          excludedResources={props.wizardState.exclude}
          setIncludedResources={(includedResources) =>
            props.setWizardState({
              ...props.wizardState,
              include: includedResources,
            })
          }
          setExcludedResources={(excludedResources) =>
            props.setWizardState({
              ...props.wizardState,
              exclude: excludedResources,
            })
          }
          resourcesScopeType={props.wizardState.resourcesScopeType}
          setResourcesScopeType={(resourcesScopeType) =>
            props.setWizardState({
              ...props.wizardState,
              resourcesScopeType,
            })
          }
          setCondition={(condition) =>
            props.setWizardState({ ...props.wizardState, condition })
          }
          supportedProperties={supportedProperties(tagsFiltersV2)}
          conditionTitle={
            DynamoDBSupport &&
            props.wizardState.policyType ===
              BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ? (
              <ConditionTitleWithResourceTypeSelector
                resourceTypes={props.wizardState.resourceTypes}
                onChange={(resourceTypes) =>
                  props.setWizardState({ ...props.wizardState, resourceTypes })
                }
              />
            ) : (
              <BackupPolicyConditionTitle
                resourceTypes={props.wizardState.resourceTypes}
                sx={{ mb: '24px' }}
              />
            )
          }
          allExcludedDescription={
            'This backup policy won’t be automatically applied to any resources. Only manually included resources will be assigned to the policy.'
          }
          allIncludedDescription={
            'This backup policy will be automatically applied to all resources. When new ones are discovered, they’ll be included too.'
          }
          manualOverridesDescription='Manually include or exclude resources from your backup policy, regardless of dynamic selection.'
        />
      </StepContainer>
    </>
  );
};

export const BackupPolicyConditionTitle = ({
  resourceTypes,
  sx,
}: {
  resourceTypes?: ResourceType[];
  sx?: SxProps;
}) => {
  return (
    <Typography variant='body1' sx={sx}>
      Back up{' '}
      {resourceTypes ? (
        <span className='font-semibold'>
          {resourceTypes?.map((x) => ResourceTypes[x].displayName).join(', ')}
          {' resources '}
        </span>
      ) : (
        'resources '
      )}
      based on these conditions:
    </Typography>
  );
};

const ConditionTitleWithResourceTypeSelector = ({
  resourceTypes,
  onChange,
}: {
  resourceTypes?: ResourceType[];
  onChange: (resourceTypes: ResourceType[]) => void;
}) => {
  return (
    <Stack direction='row' alignItems='center' gap='8px' sx={{ mb: '24px' }}>
      <Typography variant='subtitle1'>Back up</Typography>
      {
        <HighFrequencyResourceTypeSelector
          resourceTypes={resourceTypes ?? []}
          onChange={onChange}
        />
      }
      <Typography variant='subtitle1'>
        resources based on these conditions:
      </Typography>
    </Stack>
  );
};
